/**
 * Live feedback from the TypeScript engine (feedback only — never the solution).
 * Depends on `@/lib/constraints`, ported in parallel; failures degrade to "no feedback"
 * (CLAUDE.md §9: in doubt, stay silent).
 */
import type { GridSudokuDefinition, GridSudokuProgress } from "@/lib/puzzle-format/types";
import type { Feedback } from "../types";
import {
  givensOf,
  MODIFIERS_LAYER,
  REGIONS_LAYER,
  SHADING_LAYER,
  SUPERPOSITION_LAYER,
} from "./progress";

interface EngineLike {
  partialCheck(layers: { digits: Record<string, number>; [layer: string]: unknown }): {
    violations: {
      type: string;
      code: string;
      cells: string[];
      index?: number | null;
      scope?: string;
    }[];
  };
}
type EngineModule = {
  Engine: new (definition: GridSudokuDefinition, catalogue?: unknown) => EngineLike;
  mergeCatalogue?: (extra?: Record<string, unknown>) => unknown;
};

let enginePromise: Promise<EngineModule | null> | null = null;
let engineModule: EngineModule | null = null;

/** Loads the engine lazily; `localFeedback` returns [] until it is available. */
export function loadEngine(): Promise<EngineModule | null> {
  if (!enginePromise) {
    enginePromise = import("@/lib/constraints")
      .then((mod) => {
        engineModule = mod as unknown as EngineModule;
        return engineModule;
      })
      .catch(() => null);
  }
  return enginePromise;
}

export function localFeedback(
  definition: GridSudokuDefinition,
  progress: GridSudokuProgress,
  constraintDefinitions?: Record<string, unknown>,
): Feedback[] {
  if (!engineModule) {
    void loadEngine();
    return [];
  }
  try {
    const catalogue = engineModule.mergeCatalogue
      ? engineModule.mergeCatalogue(constraintDefinitions)
      : undefined;
    const engine = new engineModule.Engine(definition, catalogue);
    const shading = progress.shading ?? {};
    const regions = progress.regions ?? {};
    const modifiers = progress.modifiers ?? {};
    const seconds = Object.fromEntries(
      Object.entries(progress.superposition ?? {}).map(([cell, d]) => [cell, String(d)]),
    );
    const report = engine.partialCheck({
      digits: { ...progress.digits, ...givensOf(definition) },
      ...(Object.keys(shading).length > 0 ? { [SHADING_LAYER]: shading } : {}),
      ...(Object.keys(regions).length > 0 ? { [REGIONS_LAYER]: regions } : {}),
      ...(Object.keys(modifiers).length > 0 ? { [MODIFIERS_LAYER]: modifiers } : {}),
      ...(Object.keys(seconds).length > 0 ? { [SUPERPOSITION_LAYER]: seconds } : {}),
    });
    return report.violations.map((v) => ({
      type: v.type,
      code: v.code,
      cells: v.cells,
      index: v.index ?? null,
      scope: v.scope,
    }));
  } catch {
    return [];
  }
}
